// =============================================
// docStorage — read/migrate DMS document bytes via the blob store
// =============================================
// Bridges the app's document readers to the out-of-row blob store. A document's
// bytes are addressed by `dms_documents.file_path`:
//   • blob://… or https://… → SERVER blob volume (/mnt/blobs, via /api/dms/blob)
//                               — the durable copy; see src/lib/serverDocStore.ts
//   • idb://<key>            → local IndexedDB (offline / unauthenticated fallback)
//   • null                   → legacy row, bytes still inline in `file_base64`
//
// NEW uploads push bytes to the SERVER as soon as browser processing finishes
// (so a cleared browser never loses a pending file). `storeDocBlob` tries the
// server first and only falls back to IndexedDB when offline / logged out.
// `resolveDocBase64` reads server-first, then idb, then the inline base64,
// lazily migrating legacy rows (server-first) on first read.
// =============================================
import { isIdbKey, getDocBase64 as getBlobBase64, putDocBase64, newDocKey } from '../../../lib/docStore';
import { isServerKey, putServerBlob, getServerBlob } from '../../../lib/serverDocStore';
import { saveRow } from '../../db-viewer/data/dataAccess';
import { fetchTableRow } from '../../db-viewer/data/dbViewerService';

export { isServerKey } from '../../../lib/serverDocStore';

/**
 * Store a NEW upload's bytes and return its file_path key. Prefers the SERVER
 * blob store (`blob:…`) so the durable copy lands on the server immediately;
 * falls back to local IndexedDB (`idb://…`) when offline / not logged in. Null
 * only if everything fails (caller then relies on the inline file_base64).
 * Pass `accountOwnerId` so the server can scope the blob to that account.
 */
export async function storeDocBlob(
  base64: string,
  accountOwnerId?: number | string | null,
): Promise<string | null> {
  const serverKey = await putServerBlob(base64, accountOwnerId);
  if (serverKey) return serverKey;
  try {
    const key = newDocKey();
    await putDocBase64(key, base64);
    return key;
  } catch {
    return null;
  }
}

/**
 * Store one doc's bytes in the blob store + stamp file_path on the row. Prefers
 * the server; falls back to IndexedDB. Used by the lazy + bulk migrations.
 */
export async function migrateDocBytes(
  documentId: number,
  base64: string,
  accountOwnerId?: number | string | null,
): Promise<string | null> {
  let key = await putServerBlob(base64, accountOwnerId);
  if (!key) {
    try {
      key = newDocKey();
      await putDocBase64(key, base64);
    } catch {
      return null;
    }
  }
  await saveRow('dms_documents', documentId, { file_path: key }, 'document_id');
  return key;
}

/**
 * Resolve a document's gzip-base64 bytes from the blob store (file_path pointer),
 * falling back to the inline file_base64. Reads server-first, then idb. Lazily
 * migrates legacy rows (base64 but no blob pointer) into the blob store on first
 * read — non-blocking.
 */
export async function resolveDocBase64(row: any): Promise<string | null> {
  if (!row) return null;
  const filePath: string | null = row.file_path || null;
  if (isServerKey(filePath)) {
    const fromServer = await getServerBlob(filePath as string);
    if (fromServer != null) return fromServer;   // durable server copy
    // pointer set but unreadable (offline / not yet replicated) → fall back to base64
  } else if (isIdbKey(filePath)) {
    const fromBlob = await getBlobBase64(filePath as string);
    if (fromBlob != null) return fromBlob;
    // pointer set (e.g. by another browser) but the blob isn't on THIS device → fall back to base64
  }
  const base64: string | null = row.file_base64 || null;
  if (!base64) return null;
  if (!isServerKey(filePath) && !isIdbKey(filePath)) {
    // Legacy/dev row: move the bytes into the blob store in the background so the
    // row points at file_path and stops depending on inline base64.
    migrateDocBytes(row.document_id, base64, row.account_owner_id).catch(() => {});
  }
  return base64;
}

export interface MigrateProgress {
  done: number;
  total: number;
  migrated: number;
  skipped: number;
  failed: number;
}

/**
 * Bulk-migrate a set of documents into the blob store. Fetches each row's bytes
 * ONE at a time (never the whole table — that returns every doc's base64 and blows
 * the cache) and stamps file_path. Idempotent: rows already pointing at an idb://
 * blob, or with no bytes, are skipped.
 */
export async function migrateDocsToBlob(
  docIds: number[],
  onProgress?: (p: MigrateProgress) => void,
): Promise<MigrateProgress> {
  const p: MigrateProgress = { done: 0, total: docIds.length, migrated: 0, skipped: 0, failed: 0 };
  for (const id of docIds) {
    try {
      const row = await fetchTableRow('dms_documents', id);
      if (!row) p.failed++;
      else if (isServerKey(row.file_path) || isIdbKey(row.file_path)) p.skipped++;
      else if (!row.file_base64) p.skipped++;
      else {
        const key = await migrateDocBytes(id, row.file_base64, row.account_owner_id);
        if (key) p.migrated++; else p.failed++;
      }
    } catch {
      p.failed++;
    }
    p.done++;
    onProgress?.(p);
  }
  return p;
}
